import { useCallback, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { useToast } from '@aimsos/ui';
import { useStickyScroll } from '@aimsos/hooks';
import {
  ArchiveDashboardModal,
  CreatedFilters,
  DashboardBuilderCard,
  DashboardEmptyState,
  DashboardNoResults,
  DashboardListHeader,
  DashboardCardSkeleton,
  DashboardListTabs,
  DuplicateDashboardModal,
  ShareDashboardModal,
} from '../components';
import { DASHBOARD_STATES, SKELETON_ITEMS } from '../constants';
import { useDashboardData, useDashboardUI } from '../context';
import { useDashboardDisplayTitle } from '../hooks';
import { EMPTY_DASHBOARD_FILTERS, hasActiveDashboardFilters } from '../utils/dashboardList';

/**
 * Lists dashboards created by the organization.
 * Tab: Created (/dashboards)
 */
const DashboardsListPage = () => {
  const { t } = useTranslation('dashboards');
  const displayTitle = useDashboardDisplayTitle();
  const { dashboards, duplicateDashboard, loadingDashboards, updateDashboard } = useDashboardData();
  const fallbackDashboardName = t('common.dashboard');
  const { filters, setFilters } = useDashboardUI();
  const [archiveTargetId, setArchiveTargetId] = useState(null);
  const [duplicateTargetId, setDuplicateTargetId] = useState(null);
  const [shareTargetId, setShareTargetId] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const toast = useToast();

  const hasActiveFilters = hasActiveDashboardFilters(filters);
  const handleClearFilters = useCallback(() => setFilters(EMPTY_DASHBOARD_FILTERS), [setFilters]);

  /** Filter bar: hidden only on blank slate (no data and no search/filters). */
  const showCreatedFilters = loadingDashboards || dashboards.length > 0 || hasActiveFilters;

  const scrollRef = useRef(null);
  const { isScrolled, filtersVisible, onMouseEnter, onMouseLeave } = useStickyScroll(scrollRef);
  const showToolbar = !isScrolled || filtersVisible;
  /** No rows to show (includes search/filters with zero matches). */
  const hasNoMatchingDashboards = !loadingDashboards && dashboards.length === 0;
  const archiveTarget = dashboards.find((dashboard) => dashboard.id === archiveTargetId) || null;
  const duplicateTarget = dashboards.find((dashboard) => dashboard.id === duplicateTargetId) || null;
  const shareTarget = dashboards.find((dashboard) => dashboard.id === shareTargetId) || null;

  const showToast = useCallback(
    (severity, title, description = '') => {
      const opts = { title, message: description || undefined, canClose: true };
      if (severity === 'success') toast.success(opts);
      else if (severity === 'error') toast.error(opts);
      else toast.info(opts);
    },
    [toast],
  );

  const runArchiveStateChange = useCallback(
    async (dashboard, nextState) => {
      if (!dashboard?.id) return;
      setArchiving(true);
      try {
        const restoreState =
          dashboard.state === DASHBOARD_STATES.ARCHIVED
            ? dashboard.archivedFromState || DASHBOARD_STATES.DRAFT
            : DASHBOARD_STATES.DRAFT;

        const payload =
          nextState === DASHBOARD_STATES.ARCHIVED
            ? {
                state: nextState,
                archivedAt: new Date().toISOString(),
                archivedFromState: dashboard.state || DASHBOARD_STATES.DRAFT,
              }
            : { state: restoreState, archivedAt: null, archivedFromState: null };

        await updateDashboard(dashboard.id, payload);
        const shouldClearStateFilter =
          nextState === DASHBOARD_STATES.ARCHIVED &&
          Boolean(filters.state) &&
          filters.state !== DASHBOARD_STATES.ARCHIVED;

        if (shouldClearStateFilter) {
          setFilters({ state: '' });
        }

        if (nextState === DASHBOARD_STATES.ARCHIVED) {
          showToast(
            'success',
            t('editor.toasts.archivedSuccess', { name: displayTitle(dashboard, fallbackDashboardName) }),
          );
        } else {
          showToast(
            'success',
            t('editor.toasts.unarchivedSuccess', { name: displayTitle(dashboard, fallbackDashboardName) }),
          );
        }
      } catch (error) {
        if (nextState === DASHBOARD_STATES.ARCHIVED) {
          showToast(
            'error',
            t('editor.toasts.archiveError', { name: displayTitle(dashboard, fallbackDashboardName) }),
            t('editor.toasts.archiveErrorDescription'),
          );
        } else {
          showToast(
            'error',
            t('editor.toasts.unarchiveError', { name: displayTitle(dashboard, fallbackDashboardName) }),
            t('editor.toasts.unarchiveErrorDescription'),
          );
        }
      } finally {
        setArchiving(false);
      }
    },
    [displayTitle, fallbackDashboardName, filters, setFilters, showToast, t, updateDashboard],
  );

  const handleArchive = useCallback(
    (dashboardId) => {
      const target = dashboards.find((dashboard) => dashboard.id === dashboardId);
      if (!target) return;
      if (target.state === DASHBOARD_STATES.ARCHIVED) {
        runArchiveStateChange(target, DASHBOARD_STATES.DRAFT);
        return;
      }
      setArchiveTargetId(dashboardId);
    },
    [dashboards, runArchiveStateChange],
  );

  const handleArchiveConfirm = useCallback(async () => {
    if (!archiveTarget) return;
    await runArchiveStateChange(archiveTarget, DASHBOARD_STATES.ARCHIVED);
    setArchiveTargetId(null);
  }, [archiveTarget, runArchiveStateChange]);

  const handleArchiveClose = useCallback(() => {
    if (archiving) return;
    setArchiveTargetId(null);
  }, [archiving]);

  const handleDuplicate = useCallback((dashboardId) => {
    setDuplicateTargetId(dashboardId);
  }, []);

  const handleShare = useCallback((dashboardId) => {
    if (dashboardId) setShareTargetId(dashboardId);
  }, []);

  const handleShareClose = useCallback(() => {
    setShareTargetId(null);
  }, []);

  const handleShareConfirm = useCallback(() => {
    showToast('success', t('editor.toasts.shared'), t('editor.toasts.sharedDescription'));
  }, [showToast, t]);

  const handleDuplicateClose = useCallback(() => {
    if (duplicating) return;
    setDuplicateTargetId(null);
  }, [duplicating]);

  const handleDuplicateConfirm = useCallback(async () => {
    if (!duplicateTarget?.id) return;
    setDuplicating(true);
    try {
      await duplicateDashboard(duplicateTarget.id);
      setDuplicateTargetId(null);
      showToast(
        'success',
        t('editor.toasts.duplicated', { name: displayTitle(duplicateTarget, fallbackDashboardName) }),
      );
    } catch (error) {
      showToast(
        'error',
        t('editor.toasts.duplicateError', { name: displayTitle(duplicateTarget, fallbackDashboardName) }),
        t('editor.toasts.duplicateErrorDescription'),
      );
    } finally {
      setDuplicating(false);
    }
  }, [displayTitle, duplicateDashboard, duplicateTarget, fallbackDashboardName, showToast, t]);

  return (
    <>
      <main
        ref={scrollRef}
        className="scrollbar-aimsos-auto flex-1 overflow-y-auto bg-surface-base"
      >
        <div className="flex w-full flex-col gap-6 px-6 pb-6">
          {/* Same bar as the widget library and Agentic Studio's networks list:
              pinned, compressing once the page moves, the backdrop only then,
              and the filters folding away and returning on a held hover.

              The tabs live in the bar for ordering — they belong above the
              filters, and a sticky bar can only hold a contiguous run from the
              top — but they are the one thing that never comes back while you
              are scrolled. They are navigation, not a control you reach for
              mid-list, and the shell's sidebar already holds the navigation
              that should stay on screen. */}
          <div
            className="sticky top-0 z-20 -mx-6 px-6"
            onMouseEnter={isScrolled ? onMouseEnter : undefined}
            onMouseLeave={isScrolled ? onMouseLeave : undefined}
          >
            <div
              className={[
                'pt-6 transition-colors duration-200',
                isScrolled ? 'bg-surface-base' : '',
              ].filter(Boolean).join(' ')}
            >
              <DashboardListHeader isScrolled={isScrolled} />

              <div
                inert={isScrolled ? '' : undefined}
                className={[
                  'overflow-hidden transition-all duration-200',
                  isScrolled ? 'mt-0 max-h-0 opacity-0' : 'mt-6 max-h-20 opacity-100',
                ].join(' ')}
              >
                <DashboardListTabs />
              </div>

              {showCreatedFilters ? (
                <div
                  inert={showToolbar ? undefined : ''}
                  className={[
                    'overflow-hidden transition-all duration-200',
                    showToolbar ? 'mt-6 max-h-40 opacity-100' : 'mt-0 max-h-0 opacity-0',
                  ].join(' ')}
                >
                  <CreatedFilters />
                </div>
              ) : null}
            </div>

            {/* Cards have to leave somewhere — a hard edge reads as the header
                clipping the list rather than floating over it. */}
            {isScrolled ? (
              <div
                aria-hidden
                className="pointer-events-none h-10 bg-gradient-to-b from-surface-base to-transparent"
              />
            ) : null}
          </div>

          <section className="flex flex-col gap-3">
            {loadingDashboards ? (
              <DashboardCardSkeleton count={SKELETON_ITEMS.length} />
            ) : hasNoMatchingDashboards && hasActiveFilters ? (
              <DashboardNoResults onClearFilters={handleClearFilters} />
            ) : hasNoMatchingDashboards ? (
              <DashboardEmptyState />
            ) : (
              dashboards.map((dashboard) => (
                <DashboardBuilderCard
                  key={dashboard.id}
                  dashboard={dashboard}
                  onShare={handleShare}
                  onArchive={handleArchive}
                  onDuplicate={handleDuplicate}
                />
              ))
            )}
          </section>
        </div>
      </main>

      <ArchiveDashboardModal
        open={Boolean(archiveTarget)}
        onClose={handleArchiveClose}
        onConfirm={handleArchiveConfirm}
        dashboardName={archiveTarget ? displayTitle(archiveTarget, fallbackDashboardName) : undefined}
        loading={archiving}
      />
      <DuplicateDashboardModal
        open={Boolean(duplicateTarget)}
        onClose={handleDuplicateClose}
        onConfirm={handleDuplicateConfirm}
        dashboardName={duplicateTarget ? displayTitle(duplicateTarget, fallbackDashboardName) : undefined}
        loading={duplicating}
      />
      {shareTarget && (
        <ShareDashboardModal
          open
          onClose={handleShareClose}
          onConfirm={handleShareConfirm}
          dashboardId={shareTarget.id}
          dashboardName={displayTitle(shareTarget, fallbackDashboardName)}
          existingSharedWith={Array.isArray(shareTarget.sharedWith) ? shareTarget.sharedWith : []}
        />
      )}
    </>
  );
};

export default DashboardsListPage;
